import Button from '@material-ui/core/Button';
import { green } from '@material-ui/core/colors';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import Alert from '@material-ui/lab/Alert';
import { useSnackbar } from 'notistack';
import * as R from 'ramda';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link as RouterLink, useParams, useHistory } from 'react-router-dom';
import { SkillType } from '../../API';
import usePromiseWaiting from '../../hooks/usePromiseWaiting';
import useTechSkills from '../../hooks/useTechSkills';
import { createOrUpdateJobMatch, JobMatch, listJobMatchesByEngineer } from '../../models/job-matches';
import { getPotentialOpenPositionMatches, OpenPosition } from '../../models/jobs';
import routes from '../../routes';
import AttachResume from '../AttachResume';
import ButtonWithLoading from '../ButtonWithLoading';
import { useEngineer } from '../EngineerProvider';
import ErrorDisplay from '../ErrorDisplay';
import LoadingPage from '../Loading/Page';
import Page from '../Page';
import OpenPositionCard from './OpenPositionCad';
import PositionsCount from './PositionsCount';
import SalaryRange from './SalaryRange';

const useStyles = makeStyles((theme) => ({
  cardsContainer: {
  },
  explanation: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  alertButton: {
    // whiteSpace: 'nowrap',
  },
  buttonInterested: {
    color: green[400],
  },
  positionsCounter: {
    marginBottom: theme.spacing(1),
  },
}));

export default function PotentialJobs() {
  const classes = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [newJobMatches, setNewJobMatches] = useState<JobMatch[]>([]);
  const [markingInterest, setMarkingInterest] = useState<Record<string, boolean>>({});
  const { engineer, fetching: fetchingEngineer, reload: reloadEngineer } = useEngineer();
  const { skills, fetchingSkills } = useTechSkills();
  const {
    positionId: paramPositionId,
    interest: paramInterest,
  } = useParams<{ positionId?: string, interest?: string }>();
  const history = useHistory();

  const engineerHasResume = Boolean(engineer?.linkedIn || engineer?.cv?.key);

  const markInterested = useCallback(async (openPositionId: string, interest: boolean = true) => {
    if (interest && !engineerHasResume) {
      throw new Error('You need to first upload your resume or add your LinkedIn URL');
    }
    setMarkingInterest((previous) => ({ ...previous, [openPositionId]: true }));
    try {
      const jobMatch = await createOrUpdateJobMatch({ openPositionId, interest });
      if (jobMatch) setNewJobMatches((prev) => [...prev, jobMatch]);
    } finally {
      setMarkingInterest((previous) => ({ ...previous, [openPositionId]: false }));
    }
  }, [engineerHasResume]);

  useEffect(() => {
    if (paramPositionId) {
      const interest = paramInterest !== 'false';
      const snackKey = enqueueSnackbar('Processing...', { variant: 'info' });
      markInterested(paramPositionId, interest)
        .finally(() => closeSnackbar(snackKey))
        .then(() => {
          enqueueSnackbar(
            interest ? 'Interest marked for the position' : 'Interest removed from the position',
            { variant: interest ? 'success' : 'warning' },
          );
        })
        .catch((err) => {
          const message = err.errors?.[0]?.message || err.message;
          enqueueSnackbar(message, { variant: 'error' });
        });
      history.push(routes.jobs.potential);
    }
  }, [paramPositionId, paramInterest, history, enqueueSnackbar, closeSnackbar, markInterested]);

  const skillsById = useMemo(
    () => R.indexBy(R.prop('id'), skills || []),
    [skills],
  );

  const [loadingPositions, openPositions, errorPositions] = usePromiseWaiting(
    getPotentialOpenPositionMatches,
    [],
  );
  const [loadingMatches, jobMatches, errorMatches] = usePromiseWaiting(
    () => (engineer?.id ? listJobMatchesByEngineer(engineer.id) : null),
    [engineer?.id],
  );

  const positions = useMemo(() => R.pipe(
    () => R.concat(
      openPositions || [],
      R.pipe(
        () => R.concat(jobMatches || [], newJobMatches),
        R.map(R.prop('openPosition')),
        R.filter(R.prop('active')),
      )(),
    ),
    R.uniqBy(R.prop('id')),
    R.filter(R.pipe(
      (position: OpenPosition) => (
        (jm: JobMatch) => jm.openPositionId === position.id
      ),
      (fn) => R.find(fn, R.differenceWith(R.eqProps('id'), jobMatches || [], newJobMatches)),
      R.complement(R.allPass([R.has('status'), R.prop('status'), R.prop('engineerInterest')])),
    )),
    R.sortBy(R.path(['company', 'name']) as (a: any) => string),
  )(), [openPositions, jobMatches, newJobMatches]);

  let content = null;
  if (loadingPositions || loadingMatches || fetchingSkills || fetchingEngineer) {
    content = <LoadingPage />;
  } else if (errorPositions || errorMatches) {
    content = (
      <ErrorDisplay error="Opps, we had a problem fetching the open positions" showReload />
    );
  } else {
    const resumeAttachDialog = (
      <AttachResume
        open={resumeDialogOpen}
        onClose={async () => {
          await reloadEngineer({ noLoading: true });
          setResumeDialogOpen(false);
        }}
      />
    );
    const attachResumeAction = (
      <Button
        color="inherit"
        size="small"
        onClick={() => setResumeDialogOpen(true)}
        className={classes.alertButton}
      >
        Add Resume
      </Button>
    );
    if (positions.length) {
      content = (
        <>
          {engineerHasResume && (
            <Alert severity="info" className={classes.explanation} variant="outlined">
              <Typography variant="body1">
                Any open position you mark as &quot;Interested&quot; it&apos;s an
                {' '}
                <Link component={RouterLink} to={routes.jobs.active}>active match</Link>
                .
                This means we&apos;re preparing a custom plan to evaluate the engineering skills
                required for the marked jobs.
              </Typography>
            </Alert>
          )}
          {!engineerHasResume && (
            <Alert
              severity="warning"
              action={attachResumeAction}
              className={classes.explanation}
            >
              <Typography variant="body1">
                In order to fully review if the positions are a good fit for you, we need to take a
                look at your work experience. Please upload your resume and/or LinkedIn URL.
              </Typography>
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={5} sm={7}>
              <PositionsCount count={positions.length} className={classes.positionsCounter} />
            </Grid>
            <Grid item xs={7} sm={5}>
              <SalaryRange experience={engineer?.experience} />
            </Grid>
          </Grid>
          <Grid
            container
            className={classes.cardsContainer}
            spacing={2}
          >
            {positions.map((position) => {
              const interested = R.find(
                R.whereEq({ openPositionId: position.id, engineerInterest: true }),
                R.uniqBy(R.prop('id'), R.concat(newJobMatches, jobMatches || [])),
              );
              return (
                <OpenPositionCard
                  key={position.id}
                  position={position}
                  skills={R.filter(
                    (skill) => skill.type === SkillType.MAIN,
                    R.map(R.prop(R.__, skillsById), position.skillIds),
                  )}
                  actions={(
                    <ButtonWithLoading
                      variant="text"
                      color="primary"
                      classes={{ root: interested ? classes.buttonInterested : undefined }}
                      loading={markingInterest[position.id]}
                      onClick={() => {
                        if (!interested) {
                          markInterested(position.id)
                            .catch((error) => enqueueSnackbar(error.message, { variant: 'error' }));
                        }
                      }}
                      startIcon={interested ? <CheckIcon /> : null}
                    >
                      {interested ? 'Interested' : 'Mark interested'}
                    </ButtonWithLoading>
                  )}
                />
              );
            })}
          </Grid>
          {resumeAttachDialog}
        </>
      );
    } else {
      content = (
        <>
          <Alert
            severity="warning"
            action={!engineerHasResume ? attachResumeAction : null}
            className={classes.explanation}
          >
            {jobMatches?.length
              ? (
                <Typography variant="body1">
                  We don&apos;t have other new positions that can be a good fit
                  for you at the moment. But we&apos;ll let you know as soon as there is a new one!
                </Typography>
              ) : (
                <Typography variant="body1">
                  We don&apos;t have positions that can be a good fit for you at the moment.
                  But we&apos;ll let you know as soon as there is one!
                </Typography>
              )}
            {!engineerHasResume && (
              <Typography variant="body1">
                In the meantime, please share your resume and/or LinkedIn with us
                to have everything ready
              </Typography>
            )}
          </Alert>
          {resumeAttachDialog}
        </>
      );
    }
  }

  return (
    <Page
      title="Jobs"
      subtitle="Potential matches for you"
      action={<Button variant="text" component={RouterLink} to={routes.jobs.active}>Go to Active Matches</Button>}
    >
      {content}
    </Page>
  );
}
